import { useState, useRef, useMemo, useEffect, useCallback } from 'react';
import { requiresPurchaseDateForAkw } from '../../../utils/autoGarantie';
import { calculateWithRetry, computeCalcInputHash } from './calc';
import { AUTO_VARIANTS, type AutoVariant } from './coverages';

interface UseCoverageCalculationArgs {
  data: any;
  setData: (updater: (prev: any) => any) => void;
  buildCalculationPayload: (baseData: any, hoofddekking: AutoVariant) => Promise<any>;
  mergeResultIntoData: (bffOutput: any, prevData: any) => any;
  saveStepData: (id: string, data: any) => void;
  screenId: string;
  kwWarning: string | null;
  t: (key: string) => string;
}

const VARIANT_PREMIUMS_CACHE_KEY = 'personenauto_variant_premiums';

const readVariantPremiumsCache = (): { hash: string; premiums: Record<string, any> } | null => {
  try {
    const stored = sessionStorage.getItem(VARIANT_PREMIUMS_CACHE_KEY);
    return stored ? JSON.parse(stored) : null;
  } catch {
    return null;
  }
};

const writeVariantPremiumsCache = (hash: string, premiums: Record<string, any>) => {
  try {
    sessionStorage.setItem(VARIANT_PREMIUMS_CACHE_KEY, JSON.stringify({ hash, premiums }));
  } catch {
  }
};

export function useCoverageCalculation({
  data, setData, buildCalculationPayload, mergeResultIntoData, saveStepData, screenId, kwWarning, t,
}: UseCoverageCalculationArgs) {
  const restoredCache = useRef<{ hash: string; premiums: Record<string, any> } | null>(null);
  if (restoredCache.current === null) {
    const cache = readVariantPremiumsCache();
    restoredCache.current = cache && cache.hash === computeCalcInputHash(data) ? cache : { hash: '', premiums: {} };
  }
  const lastCalcHashRef = useRef<string>(restoredCache.current.hash);
  const calcInFlightRef = useRef<boolean>(false);
  const [isCalculating, setIsCalculating] = useState(false);
  const [calculationError, setCalculationError] = useState<string | null>(null);
  const [lastCalculationInput, setLastCalculationInput] = useState<any>(null);
  const [lastCalculationOutput, setLastCalculationOutput] = useState<any>(null);
  const [premiumsByVariant, setPremiumsByVariant] = useState<Record<string, any>>(restoredCache.current.premiums);

  useEffect(() => {
    if (lastCalcHashRef.current && Object.keys(premiumsByVariant).length > 0) {
      writeVariantPremiumsCache(lastCalcHashRef.current, premiumsByVariant);
    }
  }, [premiumsByVariant]);

  const restampVariantCache = useCallback((savedData: any) => {
    const hash = computeCalcInputHash(savedData);
    lastCalcHashRef.current = hash;
    if (Object.keys(premiumsByVariant).length > 0) {
      writeVariantPremiumsCache(hash, premiumsByVariant);
    }
  }, [premiumsByVariant]);

  // Fire 3 parallel calculations (WA, WA+, WA+volledig) with all optional coverages included
  const handleCalculateAll = useCallback(async () => {
    if (calcInFlightRef.current) return;
    calcInFlightRef.current = true;
    try {
      setIsCalculating(true);
      setCalculationError(null);

      const variants = AUTO_VARIANTS;

      // Build all 3 payloads in parallel
      const payloads = await Promise.all(
        variants.map(v => buildCalculationPayload(data, v))
      );

      // Store last input for debug (use the currently selected variant)
      const currentVariant = data?.Supportsection?.Dekkingen?.Hoofddekking || 'WA';
      const currentIdx = variants.indexOf(currentVariant);
      setLastCalculationInput(payloads[currentIdx >= 0 ? currentIdx : 0]);

      // Fire all 3 BFF calls in parallel (each with a single retry on failure)
      const results = await Promise.all(
        payloads.map(payload => calculateWithRetry(payload))
      );

      // Store results per variant
      const newPremiums: Record<string, any> = {};
      variants.forEach((v, i) => {
        if (results[i]?.output) {
          newPremiums[v] = JSON.parse(JSON.stringify(results[i].output));
        }
      });
      setPremiumsByVariant(newPremiums);

      // Store last output for debug (currently selected variant)
      setLastCalculationOutput(results[currentIdx >= 0 ? currentIdx : 0]);

      // If the selected variant is a casco coverage that the BFF refused (XG_STATUS 11,
      // e.g. UWM12 "casco niet mogelijk") while WA is still acceptable, fall back to WA —
      // the car can only be insured WA-only.
      const variantRefused = (v: string) => newPremiums[v]?.Contractdocument?.XG?.XG_STATUS === '11';
      const effectiveVariant = currentVariant !== 'WA' && variantRefused(currentVariant) && !variantRefused('WA')
        ? 'WA'
        : currentVariant;

      // Merge the effective variant's result into data
      const selectedResult = newPremiums[effectiveVariant];
      if (selectedResult) {
        setData((prevData: any) => {
          // When falling back to WA, switch the selection and reset the casco-only toggles
          // (mirrors the WA path of the coverage-card onSelect handler).
          const base = effectiveVariant === currentVariant
            ? prevData
            : {
                ...prevData,
                Supportsection: {
                  ...prevData.Supportsection,
                  Dekkingen: {
                    ...prevData.Supportsection?.Dekkingen,
                    Hoofddekking: 'WA',
                    Vrije_keuze_herstelbedrijf: 'N',
                    Nieuwwaarderegeling: 'N',
                    Aanschafwaarderegeling: 'N',
                  },
                },
              };
          const mergedData = mergeResultIntoData(selectedResult, base);
          // Stamp the hash with the post-merge value so the auto-calc effect treats this
          // state as already-calculated (mergeResultIntoData normalizes types, which would
          // otherwise shift the hash and trigger a second redundant calculation cycle).
          lastCalcHashRef.current = computeCalcInputHash(mergedData);
          setTimeout(() => saveStepData(screenId, mergedData), 0);
          return mergedData;
        });
      }

    } catch (error: any) {
      console.error('[PersonenAuto] Calculation error:', error);
      setCalculationError(error?.message || t('common.premiumCalculationError'));
      setLastCalculationOutput({ error: error?.message || String(error) });
    } finally {
      calcInFlightRef.current = false;
      setIsCalculating(false);
    }
  }, [data, saveStepData, buildCalculationPayload, mergeResultIntoData, screenId, setData, t]);

  // Recalculate a single variant (used when only CA_ERB changes for WA+volledig)
  // Uses dataRef to always read the latest state, avoiding stale closures
  const dataRef = useRef(data);
  dataRef.current = data;
  const recalculateSingleVariant = useCallback(async (variant: 'WA' | 'WA+' | 'WA+volledig') => {
    if (calcInFlightRef.current) return;
    calcInFlightRef.current = true;
    try {
      setIsCalculating(true);
      setCalculationError(null);
      const latestData = dataRef.current;
      const payload = await buildCalculationPayload(latestData, variant);
      const result = await calculateWithRetry(payload);
      if (result?.output) {
        const output = JSON.parse(JSON.stringify(result.output));
        setPremiumsByVariant(prev => ({ ...prev, [variant]: output }));
        // If this is the currently selected variant, merge into data
        const currentVariant = latestData?.Supportsection?.Dekkingen?.Hoofddekking || 'WA';
        if (currentVariant === variant) {
          setData((prevData: any) => {
            const mergedData = mergeResultIntoData(output, prevData);
            lastCalcHashRef.current = computeCalcInputHash(mergedData);
            setTimeout(() => saveStepData(screenId, mergedData), 0);
            return mergedData;
          });
        }
      }
    } catch (error: any) {
      setCalculationError(error?.message || t('common.premiumCalculationError'));
    } finally {
      calcInFlightRef.current = false;
      setIsCalculating(false);
    }
  }, [saveStepData, buildCalculationPayload, mergeResultIntoData, screenId, setData, t]);

  // Check if all required fields are filled for auto-calculation
  const areAllRequiredFieldsFilled = useCallback((dataToCheck: any): boolean => {
    const ba = dataToCheck?.Contractdocument?.PP?.BA;
    const ob = dataToCheck?.Contractdocument?.PP?.OB;

    // Address must be validated
    if (!ba?.BA_STRAAT || !ba?.BA_PLAATS) return false;

    // Key vehicle fields must be filled
    if (!ob?.OB_KENTEKE) return false;
    if (!ob?.OB_MERK) return false;
    if (!ob?.OB_MODEL) return false;
    if (!ob?.OB_BOUWJR) return false;
    if (!ob?.OB_BRANSTO) return false;
    if (ob?.OB_AANTKW === undefined || ob?.OB_AANTKW === null) return false;
    if (ob?.OB_AFMKM === undefined || ob?.OB_AFMKM === null) return false;
    if (ob?.OB_VWACA === undefined || ob?.OB_VWACA === null) return false;
    if (ob?.OB_BOUWMND === undefined || ob?.OB_BOUWMND === null) return false;
    if (!ob?.OB_TRSOORT) return false;
    if (!ob?.OB_TURBO) return false;
    if (!ob?.OB_IMPOBJ) return false;
    // OB_EERSTEI gates NW vs AKW visibility AND drives whether the BFF can price the upfront
    // value-guarantee premium (CG 2040 vs 2041). Held back until the advisor answers so the
    // first calc doesn't fire half-blind.
    if (!ob?.OB_EERSTEI) return false;

    // Schadevrije jaren must be filled
    const pp = dataToCheck?.Contractdocument?.PP;
    if (pp?.PP_SCHDVRY === undefined || pp?.PP_SCHDVRY === null) return false;

    const hoofd = dataToCheck?.Supportsection?.Dekkingen?.Hoofddekking || 'WA';
    if (hoofd === 'WA+volledig' && ob?.OB_EERSTEI === 'N') {
      if (requiresPurchaseDateForAkw(ob)) {
        if (!ob?.OB_AANKDAT) return false;
        const aankRaw = String(ob.OB_AANKDAT);
        const dattoelRaw = String(ob?.OB_DATTOEL || '');
        if (aankRaw.length === 8 && dattoelRaw.length === 8) {
          if (parseInt(aankRaw, 10) < parseInt(dattoelRaw, 10)) return false;
        }
      }
    }

    // BS fields required when driver is different from policyholder
    if ((dataToCheck?.Supportsection?.Divers?.Hoofdbestuurder ?? 'J') === 'N') {
      const bs = pp?.BS;
      if (!bs?.BS_RELSRTC || bs.BS_RELSRTC === 'A') return false;
      if (!bs?.BS_VOORL) return false;
      if (!bs?.BS_ANAAM) return false;
      if (!bs?.BS_GESLACH) return false;
      if (!bs?.BS_GEBDAT) return false;
    }

    return true;
  }, []);

  // A casco variant is "refused" when the BFF returns XG_STATUS 11 (niet acceptabel, e.g. UWM12).
  // When the casco coverages are refused but WA is acceptable, the car is WA-only.
  const isVariantRefused = (v: string): boolean =>
    premiumsByVariant?.[v]?.Contractdocument?.XG?.XG_STATUS === '11';
  const cascoRefused = !isVariantRefused('WA') && (isVariantRefused('WA+') || isVariantRefused('WA+volledig'));

  // Auto-calculate when all required fields are filled (recalculates on any input field change)
  const calcInputHash = useMemo(() => computeCalcInputHash(data), [data]);

  useEffect(() => {
    if (isCalculating) return;
    if (!areAllRequiredFieldsFilled(data)) return;
    if (kwWarning) return; // Block auto-calc when kW vs driver age is invalid
    if (calcInputHash === lastCalcHashRef.current) return;
    const timer = setTimeout(() => {
      lastCalcHashRef.current = calcInputHash;
      handleCalculateAll();
    }, 300);
    return () => clearTimeout(timer);
  }, [calcInputHash, isCalculating, areAllRequiredFieldsFilled, handleCalculateAll, data, kwWarning]);

  return {
    isCalculating,
    calculationError,
    setCalculationError,
    premiumsByVariant,
    isVariantRefused,
    cascoRefused,
    handleCalculateAll,
    recalculateSingleVariant,
    lastCalculationInput,
    lastCalculationOutput,
    calcInFlightRef,
    restampVariantCache,
  };
}
